// ---------------------------------------------------------------------------
// TanStack Query hooks for question bank API
// ---------------------------------------------------------------------------

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Question,
  QuestionAnswer,
  CreateQuestionRequest,
  UpdateQuestionRequest,
  CreateAnswerRequest,
  UpdateAnswerRequest,
  QuestionFilters,
} from '@/types/question';
import type { PaginatedResponse } from '@/types/common';

const QUESTIONS_KEY = 'questions';

export function useDiseaseAssociations() {
  return useQuery({
    queryKey: [QUESTIONS_KEY, 'disease-associations'],
    queryFn: () => api.get<string[]>('/questions/disease-associations'),
    staleTime: 5 * 60 * 1000, // 5 min — rarely changes
  });
}

function buildQueryString(filters: QuestionFilters): string {
  const params = new URLSearchParams();
  if (filters.keyword) params.set('keyword', filters.keyword);
  if (filters.diseaseAssociation) params.set('diseaseAssociation', filters.diseaseAssociation);
  // SE-API-18: standard page/pageSize pagination.
  params.set('page', String(filters.page ?? 1));
  params.set('pageSize', String(filters.pageSize ?? 25));
  return params.toString();
}

export function useQuestions(filters: QuestionFilters) {
  return useQuery({
    queryKey: [QUESTIONS_KEY, filters],
    queryFn: () =>
      api.get<PaginatedResponse<Question>>(
        `/questions?${buildQueryString(filters)}`,
      ),
  });
}

export function useQuestion(questionId: number | undefined) {
  return useQuery({
    queryKey: [QUESTIONS_KEY, questionId],
    queryFn: () => api.get<Question>(`/question?id=${questionId}`),
    enabled: !!questionId,
  });
}

export function useCreateQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateQuestionRequest) =>
      api.post<Question>('/questions', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUESTIONS_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// CMS-02 — edit + delete mutations
// ---------------------------------------------------------------------------

export function useUpdateQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      questionId,
      data,
    }: {
      questionId: number;
      data: UpdateQuestionRequest;
    }) =>
      api
        .patch<{ entries: Question[] }>(`/question`, {
          entries: [{ id: questionId, ...data }],
        })
        .then((r) => r.entries[0]),
    onSuccess: (_, { questionId }) => {
      queryClient.invalidateQueries({ queryKey: [QUESTIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUESTIONS_KEY, questionId] });
    },
  });
}

export function useDeleteQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (questionId: number) =>
      api.delete(`/question`, { entries: [{ id: questionId }] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUESTIONS_KEY] });
    },
  });
}

export function useCreateAnswer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      questionId,
      data,
    }: {
      questionId: number;
      data: CreateAnswerRequest;
    }) =>
      api
        .post<{ entries: QuestionAnswer[] }>(`/answer`, {
          entries: [{ questionId, ...data }],
        })
        .then((r) => r.entries[0]),
    onSuccess: (_, { questionId }) => {
      queryClient.invalidateQueries({ queryKey: [QUESTIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUESTIONS_KEY, questionId] });
    },
  });
}

export function useUpdateAnswer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      answerId,
      data,
    }: {
      questionId: number;
      answerId: number;
      data: UpdateAnswerRequest;
    }) =>
      api
        .patch<{ entries: QuestionAnswer[] }>(`/answer`, {
          entries: [{ id: answerId, ...data }],
        })
        .then((r) => r.entries[0]),
    onSuccess: (_, { questionId }) => {
      queryClient.invalidateQueries({ queryKey: [QUESTIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUESTIONS_KEY, questionId] });
    },
  });
}

export function useDeleteAnswer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      answerId,
    }: {
      questionId: number;
      answerId: number;
    }) => api.delete(`/answer`, { entries: [{ id: answerId }] }),
    onSuccess: (_, { questionId }) => {
      queryClient.invalidateQueries({ queryKey: [QUESTIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUESTIONS_KEY, questionId] });
    },
  });
}
